import React, { useState } from 'react';
import {
  Box,
  Paper,
  Button,
  Typography,
  Container,
  Grid,
  Card,
  CardContent,
  Divider,
  Chip
} from '@mui/material';
import {
  Business,
  AdminPanelSettings,
  Security,
  Analytics,
  People,
  Settings,
  Star,
  CheckCircle
} from '@mui/icons-material';
import SimpleAuth from './SimpleAuth';
import AdminLogin from './AdminLogin';

const AuthPortal = ({ onLogin }) => {
  console.log('AuthPortal rendering...');
  const [currentView, setCurrentView] = useState('portal'); // portal, client-auth, admin-login
  const [userType, setUserType] = useState('');

  const handleClientAuth = (client, token) => {
    console.log('Client auth:', client);
    onLogin(client, token);
  };

  const handleAdminLogin = (admin, token) => {
    console.log('Admin login:', admin);
    onLogin(admin, token);
  };

  const handleSwitchToClientAuth = () => {
    setCurrentView('client-auth');
  };

  const handleSwitchToAdminAuth = () => {
    setCurrentView('admin-login');
  };

  const handleBackToPortal = () => {
    setCurrentView('portal');
  };

  const clientFeatures = [
    {
      title: 'AI Content Generation',
      description: 'Create engaging content with AI assistance',
      icon: <Star />
    },
    {
      title: 'Multi-Platform Management',
      description: 'Manage all your social media accounts in one place',
      icon: <Business />
    },
    {
      title: 'Advanced Analytics',
      description: 'Track performance and optimize your strategy',
      icon: <Analytics />
    },
    {
      title: 'Ad Campaign Management',
      description: 'Create and manage effective ad campaigns',
      icon: <People />
    }
  ];

  const adminFeatures = [
    {
      title: 'Client Management',
      description: 'Manage all client accounts and subscriptions',
      icon: <People />
    },
    {
      title: 'Platform Analytics',
      description: 'Monitor platform performance and revenue',
      icon: <Analytics />
    },
    {
      title: 'Security Controls',
      description: 'Manage access controls and security settings',
      icon: <Security />
    },
    {
      title: 'System Administration',
      description: 'Configure platform settings and integrations',
      icon: <Settings />
    }
  ];

  if (currentView === 'client-auth') {
    return (
      <Box sx={{ width: '100%' }}>
        <Button 
          variant="text" 
          onClick={handleBackToPortal}
          sx={{ mb: 2 }}
        >
          ← Back to Portal
        </Button>
        <SimpleAuth onLogin={handleClientAuth} />
      </Box>
    );
  }

  if (currentView === 'admin-login') {
    return (
      <Box sx={{ width: '100%' }}>
        <Button 
          variant="text" 
          onClick={handleBackToPortal}
          sx={{ mb: 2 }}
        >
          ← Back to Portal
        </Button>
        <AdminLogin onLogin={handleAdminLogin} />
      </Box>
    );
  }

  return (
    <Container component="main" maxWidth="lg">
      <Box sx={{ marginTop: 8, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
        <Paper elevation={3} sx={{ padding: 4, display: 'flex', flexDirection: 'column', alignItems: 'center', width: '100%' }}>
          <Typography component="h1" variant="h3" gutterBottom>
            Welcome to Social Media Enhancement Tool
          </Typography>
          
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4, textAlign: 'center' }}>
            Choose your access type to get started
          </Typography>

          <Grid container spacing={4} sx={{ width: '100%', mb: 4 }}>
            {/* Client Access */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ backgroundColor: 'primary.main', borderRadius: '50%', p: 2, mb: 2, display: 'inline-flex' }}>
                    <Business sx={{ color: 'white', fontSize: 40 }} />
                  </Box>
                  
                  <Typography variant="h5" gutterBottom>
                    Client Access
                  </Typography>
                  
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Access your social media dashboard and management tools
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {clientFeatures.map((feature, index) => (
                      <Grid item xs={6} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {feature.icon}
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {feature.title}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Button
                    variant="contained"
                    size="large"
                    fullWidth
                    onClick={handleSwitchToClientAuth}
                    sx={{ mb: 2 }}
                  >
                    Sign In as Client
                  </Button>

                  <Typography variant="body2" color="text.secondary">
                    Demo: demo@example.com / demo123
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            {/* Admin Access */}
            <Grid item xs={12} md={6}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                <CardContent sx={{ flexGrow: 1, textAlign: 'center' }}>
                  <Box sx={{ backgroundColor: 'error.main', borderRadius: '50%', p: 2, mb: 2, display: 'inline-flex' }}>
                    <AdminPanelSettings sx={{ color: 'white', fontSize: 40 }} />
                  </Box>
                  
                  <Typography variant="h5" gutterBottom>
                    Admin Access
                  </Typography>
                  
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Platform administration and management tools
                  </Typography>

                  <Grid container spacing={2} sx={{ mb: 3 }}>
                    {adminFeatures.map((feature, index) => (
                      <Grid item xs={6} key={index}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                          {feature.icon}
                          <Typography variant="body2" sx={{ ml: 1 }}>
                            {feature.title}
                          </Typography>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>

                  <Button
                    variant="outlined"
                    size="large"
                    fullWidth
                    onClick={handleSwitchToAdminAuth}
                    sx={{ mb: 2 }}
                  >
                    Admin Portal
                  </Button>

                  <Typography variant="body2" color="text.secondary">
                    Demo: admin@example.com / admin123
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          <Divider sx={{ width: '100%', my: 2 }}>
            <Typography variant="body2" color="text.secondary">
              Platform Features
            </Typography>
          </Divider>

          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Secure, scalable social media management platform
            </Typography>
            <Chip 
              label="Production Ready" 
              color="success" 
              icon={<CheckCircle />}
              sx={{ mt: 1 }}
            />
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};

export default AuthPortal; 